import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CarOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../services/axiosInstance";
import {
  GET_ACTIVE_RAKE_TRIPS,
  GET_TRIPS_BY_RAKE,
} from "../../services/apiEndPoints";
import { ITrip, TripStatus } from "../types/trip.types";
import TripDetailDrawer from "./TripDetailDrawer";

dayjs.extend(relativeTime);

const { Text } = Typography;
const { Search } = Input;

const STATUS_CONFIG: Record<
  TripStatus,
  { label: string; color: string; badge: "success" | "processing" | "default" | "error" | "warning" }
> = {
  pending: { label: "Pending", color: "default", badge: "default" },
  tare_weighed: { label: "Tare Weighed", color: "blue", badge: "processing" },
  loading: { label: "Loading", color: "orange", badge: "processing" },
  gross_weighed: { label: "Gross Weighed", color: "cyan", badge: "processing" },
  dispatched: { label: "Dispatched", color: "purple", badge: "processing" },
  delivered: { label: "Delivered", color: "green", badge: "success" },
  completed: { label: "Completed", color: "success", badge: "success" },
};

const UPLOAD_KEYS = [
  "vehicle_gatepass",
  "weightment_slip",
  "final_weightment",
  "surveyor_seal",
  "driver_arrival",
  "challan_slip",
  "truck_sheet",
  "form_515",
  "wharfage_demurrage",
] as const;

const uploadCount = (trip: ITrip) =>
  UPLOAD_KEYS.filter((k) => !!trip.uploads?.[k]).length;

interface TripListComponentProps {
  rakeMasterId?: string;
}

const TripListComponent = ({ rakeMasterId }: TripListComponentProps) => {
  const [trips, setTrips] = useState<ITrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TripStatus | "">("");
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeMasterId, setActiveMasterId] = useState<string | null>(null);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const url = rakeMasterId
        ? GET_TRIPS_BY_RAKE(rakeMasterId)
        : GET_ACTIVE_RAKE_TRIPS;
      const res = await axios.get(url);
      const data = res?.data?.data ?? [];
      setTrips(Array.isArray(data) ? data : []);
      setActiveMasterId(res?.data?.rakeMasterId ?? null);
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        message.error("Failed to load trips");
      }
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  const openDrawer = (tripId: string) => {
    setSelectedTripId(tripId);
    setDrawerOpen(true);
  };

  const filtered = trips.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      t.vehicleNumber.toLowerCase().includes(q) ||
      t.driverName.toLowerCase().includes(q) ||
      t.wagonNo.toLowerCase().includes(q) ||
      (t.unloadingDestination || "").toLowerCase().includes(q);
    const matchStatus = !statusFilter || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const columns: ColumnsType<ITrip> = [
    {
      title: "Vehicle",
      dataIndex: "vehicleNumber",
      key: "vehicleNumber",
      width: 140,
      render: (v: string) => (
        <Space size={6}>
          <CarOutlined style={{ color: "#1677ff" }} />
          <Text strong style={{ fontFamily: "monospace", fontSize: 13 }}>
            {v}
          </Text>
        </Space>
      ),
    },
    {
      title: "Wagon",
      dataIndex: "wagonNo",
      key: "wagonNo",
      width: 120,
      render: (v: string) => (
        <Text style={{ fontFamily: "monospace", fontSize: 12, color: "#475569" }}>
          {v || "—"}
        </Text>
      ),
    },
    {
      title: "Driver",
      key: "driver",
      width: 160,
      render: (_: unknown, r: ITrip) => (
        <div>
          <Text style={{ fontSize: 13 }}>{r.driverName || "—"}</Text>
          {r.driverPhone && (
            <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
              {r.driverPhone}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Destination",
      dataIndex: "unloadingDestination",
      key: "destination",
      width: 150,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "Net Wt (kg)",
      dataIndex: "netWeight",
      key: "netWeight",
      width: 110,
      align: "right" as const,
      render: (v: number) =>
        v ? (
          <Text strong style={{ color: "#0f766e", fontSize: 13 }}>
            {v.toLocaleString()}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Bags",
      dataIndex: "bagsDelivered",
      key: "bags",
      width: 80,
      align: "right" as const,
      render: (v: number) => (
        <Text style={{ fontSize: 12 }}>{v ?? "—"}</Text>
      ),
    },
    {
      title: "Docs",
      key: "docs",
      width: 80,
      align: "center" as const,
      render: (_: unknown, r: ITrip) => {
        const count = uploadCount(r);
        return (
          <Badge
            count={count}
            showZero
            style={{
              backgroundColor: count === 9 ? "#16a34a" : count > 0 ? "#1677ff" : "#94a3b8",
            }}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (s: TripStatus) => {
        const cfg = STATUS_CONFIG[s] || { label: s, color: "default" };
        return (
          <Tag color={cfg.color} style={{ borderRadius: 6, fontSize: 11 }}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "unloadingDate",
      key: "date",
      width: 100,
      render: (v: string) =>
        v ? (
          <Tooltip title={dayjs(v).format("MMM D, YYYY")}>
            <Text style={{ fontSize: 12, color: "#64748b" }}>
              {dayjs(v).format("MMM D")}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: unknown, r: ITrip) => (
        <Tooltip title="View details">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDrawer(r.tripId)}
            style={{ color: "#1677ff" }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {activeMasterId && !rakeMasterId && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 16,
            fontSize: 12,
            color: "#166534",
          }}
        >
          Showing trips for active rake:{" "}
          <Text strong style={{ fontSize: 12, color: "#166534" }}>
            {activeMasterId}
          </Text>
        </div>
      )}

      <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
        <Col flex="1">
          <Search
            placeholder="Search vehicle, driver, wagon, destination…"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 380 }}
          />
        </Col>
        <Col>
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 160 }}
            placeholder="All statuses"
            options={[
              { value: "", label: "All statuses" },
              ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({
                value: k,
                label: v.label,
              })),
            ]}
          />
        </Col>
        <Col>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTrips}
              loading={loading}
            />
          </Tooltip>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary">Loading trips…</Text>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          description={
            trips.length === 0
              ? "No trips found for this rake"
              : "No trips match your search"
          }
          style={{ padding: 48 }}
        />
      ) : (
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="tripId"
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 900 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {filtered.length} trip{filtered.length !== 1 ? "s" : ""}
                    {filtered.length !== trips.length
                      ? ` (filtered from ${trips.length})`
                      : ""}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong style={{ fontSize: 12, color: "#0f766e" }}>
                    {filtered
                      .reduce((sum, t) => sum + (t.netWeight || 0), 0)
                      .toLocaleString()}{" "}
                    kg
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong style={{ fontSize: 12 }}>
                    {filtered.reduce(
                      (sum, t) => sum + (t.bagsDelivered || 0),
                      0
                    )}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} colSpan={3} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}

      <TripDetailDrawer
        open={drawerOpen}
        tripId={selectedTripId}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTripId(null);
        }}
      />
    </div>
  );
};

export default TripListComponent;
